import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

/**
 * UI Store - manages UI state like sidebar visibility, theme preferences, etc.
 */

interface UIState {
  // Sidebar collapsed state
  sidebarCollapsed: boolean
  // Mobile menu open state
  mobileMenuOpen: boolean
  // Chat list collapsed on mobile (showing chat room instead)
  chatListCollapsed: boolean
  // Notification popup preferences (v1-compatible)
  suppressNotifications: boolean
  // Expert view mode (v1-compatible)
  expertView: boolean
}

interface UIActions {
  // Toggle sidebar
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
  // Toggle mobile menu
  toggleMobileMenu: () => void
  setMobileMenuOpen: (open: boolean) => void
  // Toggle chat list on mobile
  toggleChatList: () => void
  setChatListCollapsed: (collapsed: boolean) => void
  // Toggle notification popups
  toggleNotifications: () => void
  setNotifications: (enabled: boolean) => void
  // Toggle expert view
  toggleExpertView: () => void
  setExpertView: (enabled: boolean) => void
  // Initialize from v1 localStorage
  initializeFromV1: (branch: string) => void
}

type UIStore = UIState & UIActions

export const useUIStore = create<UIStore>()(
  persist(
    (set, get) => ({
      // Initial state
      sidebarCollapsed: false,
      mobileMenuOpen: false,
      chatListCollapsed: false,
      suppressNotifications: false,
      expertView: false,

      // Sidebar actions
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

      // Mobile menu actions
      toggleMobileMenu: () => set((state) => ({ mobileMenuOpen: !state.mobileMenuOpen })),
      setMobileMenuOpen: (open) => set({ mobileMenuOpen: open }),

      // Chat list actions
      toggleChatList: () => set((state) => ({ chatListCollapsed: !state.chatListCollapsed })),
      setChatListCollapsed: (collapsed) => set({ chatListCollapsed: collapsed }),

      // Notification actions
      toggleNotifications: () => {
        const newValue = !get().suppressNotifications
        set({ suppressNotifications: newValue })
      },
      setNotifications: (enabled) => set({ suppressNotifications: !enabled }),

      // Expert view actions
      toggleExpertView: () => {
        const newValue = !get().expertView
        set({ expertView: newValue })
      },
      setExpertView: (enabled) => set({ expertView: enabled }),

      // Initialize from v1 localStorage keys
      initializeFromV1: (branch: string) => {
        const suppressNotifications =
          localStorage.getItem(`${branch}-suppress-notifications`) === '1'
        const expertView = localStorage.getItem(`${branch}-expert-view`) === '1'

        set({
          suppressNotifications,
          expertView,
        })
      },
    }),
    {
      name: 'alpha-trader-v2-ui',
      storage: createJSONStorage(() => localStorage),
    }
  )
)
